import {
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Typography,
    alpha,
    FormControlLabel,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    SocialPlatform,
    PLATFORM_INFO,
} from "../../../api/services/socialMediaService";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

const premiumCard = {
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
    overflow: "hidden",
};

const PLATFORMS = Object.keys(PLATFORM_INFO);

const SocialPlatforms = () => {
    const queryClient = useQueryClient();
    const [connectOpen, setConnectOpen] = useState(false);
    const [editPlatform, setEditPlatform] = useState<SocialPlatform | null>(null);
    const [formData, setFormData] = useState({
        platform: "",
        account_name: "",
        access_token: "",
        refresh_token: "",
        token_expires: "",
        platform_id: "",
        metadata: "",
    });

    const { data, isLoading } = useQuery("social-platforms", () =>
        socialMediaService.listPlatforms()
    );

    const connectMutation = useMutation(
        (data: any) => socialMediaService.connectPlatform(data),
        {
            onSuccess: () => {
                toast.success("Platform connected!");
                queryClient.invalidateQueries("social-platforms");
                handleClose();
            },
            onError: () => {
                toast.error("Failed to connect platform");
            },
        }
    );

    const updateMutation = useMutation(
        ({ id, data }: { id: number; data: any }) =>
            socialMediaService.updatePlatform(id, data),
        {
            onSuccess: () => {
                toast.success("Platform updated!");
                queryClient.invalidateQueries("social-platforms");
                handleClose();
            },
            onError: () => {
                toast.error("Failed to update platform");
            },
        }
    );

    const deleteMutation = useMutation(
        (id: number) => socialMediaService.deletePlatform(id),
        {
            onSuccess: () => {
                toast.success("Platform disconnected");
                queryClient.invalidateQueries("social-platforms");
            },
            onError: () => {
                toast.error("Failed to delete platform");
            },
        }
    );

    const toggleMutation = useMutation(
        ({ id, is_active }: { id: number; is_active: boolean }) =>
            socialMediaService.togglePlatform(id, is_active),
        {
            onSuccess: () => queryClient.invalidateQueries("social-platforms"),
        }
    );

    const validateMutation = useMutation(
        () => socialMediaService.validatePlatforms(),
        {
            onSuccess: (res) => {
                const results = res.data;
                const valid = results?.filter((r: any) => r.valid).length || 0;
                const invalid = results?.filter((r: any) => !r.valid).length || 0;
                toast.success(`Validation: ${valid} valid, ${invalid} invalid`);
                queryClient.invalidateQueries("social-platforms");
            },
            onError: () => {
                toast.error("Validation failed");
            },
        }
    );

    const handleClose = () => {
        setConnectOpen(false);
        setEditPlatform(null);
        setFormData({
            platform: "",
            account_name: "",
            access_token: "",
            refresh_token: "",
            token_expires: "",
            platform_id: "",
            metadata: "",
        });
    };

    const handleEdit = (p: SocialPlatform) => {
        setEditPlatform(p);
        setFormData({
            platform: p.platform,
            account_name: p.account_name,
            access_token: "",
            refresh_token: "",
            token_expires: p.token_expires || "",
            platform_id: p.platform_id || "",
            metadata: p.metadata ? JSON.stringify(p.metadata, null, 2) : "",
        });
        setConnectOpen(true);
    };

    const handleSubmit = () => {
        let metadata: Record<string, any> | undefined;
        if (formData.metadata) {
            try {
                metadata = JSON.parse(formData.metadata);
            } catch {
                toast.error("Invalid JSON in metadata field");
                return;
            }
        }

        const payload: any = {
            platform: formData.platform,
            account_name: formData.account_name,
            platform_id: formData.platform_id || undefined,
            token_expires: formData.token_expires || undefined,
            metadata,
        };

        if (formData.access_token) payload.access_token = formData.access_token;
        if (formData.refresh_token) payload.refresh_token = formData.refresh_token;

        if (editPlatform) {
            updateMutation.mutate({ id: editPlatform.id, data: payload });
        } else {
            if (!formData.access_token) {
                toast.error("Access token is required");
                return;
            }
            connectMutation.mutate({ ...payload, access_token: formData.access_token });
        }
    };

    const handleDelete = (p: SocialPlatform) => {
        if (
            window.confirm(
                `Disconnect ${PLATFORM_INFO[p.platform]?.name || p.platform}?`
            )
        ) {
            deleteMutation.mutate(p.id);
        }
    };

    const connectedPlatformNames = (data?.data || []).map(
        (p: SocialPlatform) => p.platform
    );
    const availablePlatforms = PLATFORMS.filter(
        (p) => !connectedPlatformNames.includes(p)
    );

    if (isLoading) return <Loader />;

    const platforms: SocialPlatform[] = data?.data || [];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                        Social Platforms
                    </Typography>
                    <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                        Connect and manage your social media accounts
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<VerifiedRoundedIcon />}
                        onClick={() => validateMutation.mutate()}
                        disabled={validateMutation.isLoading}
                    >
                        Validate All
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setConnectOpen(true)}
                        disabled={availablePlatforms.length === 0}
                    >
                        Connect Platform
                    </Button>
                </Box>
            </Box>

            {/* Connected Platforms */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
                {platforms.map((p) => {
                    const info = PLATFORM_INFO[p.platform] || {
                        name: p.platform,
                        color: "#666",
                        icon: "?",
                        setupSteps: [],
                    };
                    const tokenExpires = p.token_expires
                        ? new Date(p.token_expires)
                        : null;
                    const daysLeft = tokenExpires
                        ? Math.ceil(
                            (tokenExpires.getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                        )
                        : null;
                    const isExpired = daysLeft !== null && daysLeft <= 0;

                    return (
                        <Grid item xs={12} md={6} lg={4} key={p.id}>
                            <Card sx={premiumCard}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                                        background: `linear-gradient(135deg, ${alpha(
                                            info.color,
                                            0.02
                                        )} 0%, ${alpha(info.color, 0.06)} 100%)`,
                                    }}
                                >
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <Box display="flex" alignItems="center" gap={1.5}>
                                            <Box
                                                sx={{
                                                    width: 44,
                                                    height: 44,
                                                    borderRadius: "12px",
                                                    backgroundColor: alpha(info.color, 0.1),
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "22px",
                                                    border: `1px solid ${alpha(info.color, 0.15)}`,
                                                }}
                                            >
                                                {info.icon}
                                            </Box>
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: "15px" }}>
                                                    {info.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                                                    {p.account_name}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={p.is_active}
                                                    onChange={(e) =>
                                                        toggleMutation.mutate({
                                                            id: p.id,
                                                            is_active: e.target.checked,
                                                        })
                                                    }
                                                    size="small"
                                                />
                                            }
                                            label=""
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ p: 2 }}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        mb={1.5}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            {isExpired ? (
                                                <Chip
                                                    icon={<ErrorRoundedIcon sx={{ fontSize: 14 }} />}
                                                    label="Token Expired"
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#FEF2F2",
                                                        color: "#DC2626",
                                                        fontWeight: 600,
                                                        fontSize: "11px",
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={
                                                        <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                                                    }
                                                    label={
                                                        daysLeft !== null
                                                            ? `Valid (${daysLeft}d left)`
                                                            : "Connected"
                                                    }
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#F0FDF4",
                                                        color: "#16A34A",
                                                        fontWeight: 600,
                                                        fontSize: "11px",
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box>
                                            <IconButton size="small" onClick={() => handleEdit(p)}>
                                                <EditRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(p)}
                                            >
                                                <DeleteRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {p.platform_id && (
                                        <Typography
                                            sx={{ fontSize: "11px", color: "#94A3B8", mb: 0.5 }}
                                        >
                                            Platform ID: {p.platform_id}
                                        </Typography>
                                    )}
                                    <Typography sx={{ fontSize: "11px", color: "#94A3B8" }}>
                                        Connected:{" "}
                                        {new Date(p.created_at).toLocaleDateString()}
                                    </Typography>
                                </Box>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Setup Guides */}
            <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2, color: "#1E293B" }}
            >
                Platform Setup Guides
            </Typography>
            {PLATFORMS.map((platform) => {
                const info = PLATFORM_INFO[platform];
                const isConnected = connectedPlatformNames.includes(platform);
                return (
                    <Accordion
                        key={platform}
                        sx={{
                            mb: 1,
                            borderRadius: "12px !important",
                            "&::before": { display: "none" },
                            border: "1px solid rgba(226, 232, 240, 0.6)",
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                            <Box display="flex" alignItems="center" gap={1.5} flex={1}>
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "8px",
                                        backgroundColor: alpha(info.color, 0.08),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "16px",
                                    }}
                                >
                                    {info.icon}
                                </Box>
                                <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
                                    {info.name}
                                </Typography>
                                {isConnected && (
                                    <Chip
                                        icon={<LinkRoundedIcon sx={{ fontSize: 12 }} />}
                                        label="Connected"
                                        size="small"
                                        sx={{
                                            backgroundColor: "#F0FDF4",
                                            color: "#16A34A",
                                            fontWeight: 600,
                                            fontSize: "10px",
                                        }}
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stepper orientation="vertical">
                                {info.setupSteps.map((step, i) => (
                                    <Step key={i} active>
                                        <StepLabel>
                                            <Typography sx={{ fontSize: "13px" }}>{step}</Typography>
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            {!isConnected && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    sx={{ mt: 2 }}
                                    onClick={() => {
                                        setFormData((f) => ({ ...f, platform }));
                                        setConnectOpen(true);
                                    }}
                                >
                                    Connect {info.name}
                                </Button>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {/* Connect/Edit Dialog */}
            <Dialog
                open={connectOpen || !!editPlatform}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {editPlatform
                        ? `Edit ${PLATFORM_INFO[editPlatform.platform]?.name ||
                        editPlatform.platform
                        }`
                        : "Connect Platform"}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                        {!editPlatform && (
                            <FormControl fullWidth size="small">
                                <InputLabel>Platform</InputLabel>
                                <Select
                                    value={formData.platform}
                                    onChange={(e) =>
                                        setFormData((f) => ({ ...f, platform: e.target.value }))
                                    }
                                    label="Platform"
                                >
                                    {availablePlatforms.map((p) => (
                                        <MenuItem key={p} value={p}>
                                            {PLATFORM_INFO[p].icon} {PLATFORM_INFO[p].name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <TextField
                            label="Account Name"
                            value={formData.account_name}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, account_name: e.target.value }))
                            }
                            size="small"
                            placeholder="e.g. @talaqah_app"
                        />
                        <TextField
                            label={editPlatform ? "Access Token (leave empty to keep)" : "Access Token"}
                            value={formData.access_token}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, access_token: e.target.value }))
                            }
                            size="small"
                            type="password"
                            required={!editPlatform}
                        />
                        <TextField
                            label="Refresh Token"
                            value={formData.refresh_token}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, refresh_token: e.target.value }))
                            }
                            size="small"
                            type="password"
                            helperText="Required for Twitter, LinkedIn, Pinterest, Google Business"
                        />
                        <TextField
                            label="Platform ID"
                            value={formData.platform_id}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, platform_id: e.target.value }))
                            }
                            size="small"
                            helperText="Page ID, Account ID, Author ID, etc."
                        />
                        <TextField
                            label="Token Expiry"
                            value={formData.token_expires}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, token_expires: e.target.value }))
                            }
                            size="small"
                            type="datetime-local"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="Metadata (JSON)"
                            value={formData.metadata}
                            onChange={(e) =>
                                setFormData((f) => ({ ...f, metadata: e.target.value }))
                            }
                            size="small"
                            multiline
                            rows={3}
                            placeholder='{"page_id": "...", "board_id": "..."}'
                            helperText="Platform-specific settings in JSON format"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            connectMutation.isLoading ||
                            updateMutation.isLoading ||
                            (!editPlatform && (!formData.platform || !formData.account_name))
                        }
                    >
                        {editPlatform ? "Update" : "Connect"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SocialPlatforms;
